import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useUserStore } from '../stores/StoreProvider';
import auth0Service from '../services/auth0Service';
import logo from '../assets/logo.png';
import { 
  Home, 
  Trophy, 
  Target, 
  MessageCircle, 
  User, 
  Users, 
  Calendar,
  Plus,
  LogOut,
  Menu,
  X,
  Award
} from 'lucide-react';

const Layout = observer(() => {
  const user = useUserStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: Home },
    { name: 'Career', href: '/app/games', icon: Trophy },
    { name: 'Chat', href: '/app/chat', icon: MessageCircle },
    { name: 'Profile', href: '/app/profile', icon: User },
    { name: 'Friends', href: '/app/friends', icon: Users },
    { name: 'Season', href: '/app/season', icon: Award },
  ];

  const handleLogout = () => {
    auth0Service.logout();
    user.clearAuthState();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" onClick={() => setSidebarOpen(false)} />
        <div className={`fixed inset-y-0 left-0 flex w-64 flex-col bg-white transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200">
            <div className="flex items-center">
              <img src={logo} alt="Biscuit" className="w-8 h-8" />
              <span className="ml-2 text-lg font-semibold text-gray-900">Biscuit</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* User info in mobile sidebar */}
          <div className="px-4 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                {user.avatar ? (
                  <img src={user.avatar} alt="Avatar" className="w-10 h-10 rounded-full" />
                ) : (
                  <User size={20} className="text-gray-600" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
            </div>
          </div>
          
          <nav className="flex-1 px-4 py-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-100 text-blue-700 shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon size={20} className="mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          
          <div className="border-t border-gray-200 p-4 space-y-2">
            <Link
              to="/app/add-game"
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <Plus size={20} className="mr-3" />
              Add Game
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut size={20} className="mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4">
          <img src={logo} alt="Biscuit" className="w-8 h-8" />

            <span className="ml-2 text-lg font-semibold">Biscuit</span>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <item.icon size={20} className="mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-gray-200 p-4">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <LogOut size={20} className="mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
              >
                <Menu size={20} />
              </button>
              <div className="hidden lg:flex items-center">
                <img src={logo} alt="Biscuit" className="w-8 h-8" />
                <span className="ml-2 text-lg font-semibold text-gray-900">Biscuit</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link
                to="/app/add-game"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                <Plus size={16} className="mr-2" />
                <span className="hidden sm:inline">Add Game</span>
              </Link>
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  {user.avatar ? (
                    <img src={user.avatar} alt="Avatar" className="w-8 h-8 rounded-full" />
                  ) : (
                    <User size={16} className="text-gray-600" />
                  )}
                </div>
                <span className="hidden sm:inline text-sm font-medium text-gray-700">{user.displayName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className={location.pathname === '/app/chat' ? 'h-screen' : 'py-6'}>
          <div className={location.pathname === '/app/chat' ? 'h-full' : 'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
});

export default Layout; 